// src/config/rolePermissionExpectations.js
//
// SINGLE SOURCE OF TRUTH for what each role is *intended* to be able to do.
//
// This file is hand-maintained by product/engineering and describes the
// EXPECTED behavior. The contract tests in tests/role-permissions.test.js
// assert that the actual permission code (config/roles.js PERMISSIONS_MATRIX,
// canEditStudent(), etc.) agrees with these expectations for EVERY role.
//
// When you intentionally change what a role can do, update THIS file first,
// then make the code match until the tests go green. If a test fails without a
// matching change here, that's drift (a bug) — exactly the class of issue that
// produced the admin_504 504-coordinator regression.
//
// ── Capability vocabulary ──────────────────────────────────────────────────
// adminActions: the set of admin-panel-level actions the role should have
//   (must be a subset of PERMISSION_ACTIONS keys, expressed as their values).
//
// edit: how the role may edit a STUDENT record (the data-write layer):
//   scope:
//     'any'      → may edit any student
//     'planType' → may edit any student whose plan === `plan` (NOT caseload-bound)
//     'caseload' → may edit only students on their own caseload
//                  (optionally further constrained to `plan`)
//     'none'     → may not edit students
//   plan: null | 'IEP' | '504' (plan constraint, when applicable)

import { PERMISSION_ACTIONS as A } from './roles'

export const ROLE_CAPABILITY_EXPECTATIONS = {
  admin: {
    label: 'Admin',
    adminActions: [
      A.VIEW_USERS,
      A.EDIT_USER,
      A.DELETE_USER,
      A.ACCESS_ADMIN_PANEL,
      A.MANAGE_AIDES,
      A.MANAGE_SYSTEM_SETTINGS,
      A.MANAGE_TEACHER_FEEDBACK,
      A.MANAGE_IMPORTS,
      A.MANAGE_BACKUPS
    ],
    edit: { scope: 'any', plan: null }
  },

  district_admin: {
    label: 'District Admin',
    adminActions: [
      A.VIEW_USERS,
      A.EDIT_USER,
      A.DELETE_USER,
      A.ACCESS_ADMIN_PANEL,
      A.MANAGE_AIDES,
      A.MANAGE_SYSTEM_SETTINGS,
      A.MANAGE_TEACHER_FEEDBACK,
      A.MANAGE_IMPORTS,
      A.MANAGE_BACKUPS
    ],
    edit: { scope: 'any', plan: null }
  },

  district_coordinator: {
    label: 'District Coordinator',
    adminActions: [A.VIEW_USERS, A.EDIT_USER, A.ACCESS_ADMIN_PANEL, A.MANAGE_IMPORTS],
    edit: { scope: 'none', plan: null }
  },

  school_admin: {
    label: 'School Administrator',
    // Everything except system settings.
    adminActions: [
      A.VIEW_USERS,
      A.EDIT_USER,
      A.DELETE_USER,
      A.ACCESS_ADMIN_PANEL,
      A.MANAGE_AIDES,
      A.MANAGE_TEACHER_FEEDBACK,
      A.MANAGE_IMPORTS,
      A.MANAGE_BACKUPS
    ],
    edit: { scope: 'any', plan: null }
  },

  staff_view: {
    label: 'Staff Viewer',
    adminActions: [A.VIEW_USERS],
    edit: { scope: 'none', plan: null }
  },

  staff_edit: {
    label: 'Staff Edit (Full)',
    adminActions: [A.VIEW_USERS],
    edit: { scope: 'any', plan: null }
  },

  staff_edit_docs: {
    label: 'Staff Edit (Docs Only)',
    adminActions: [A.VIEW_USERS],
    // canEditStudent() returns true; the docs/flags-only narrowing is enforced
    // separately client-side, not at the canEditStudent gate.
    edit: { scope: 'any', plan: null }
  },

  admin_504: {
    label: '504 Coordinator',
    // Full peer of school_admin (same admin actions; everything except
    // MANAGE_SYSTEM_SETTINGS). The only functional difference is that admin_504
    // also has a caseload (appears in the CM dropdown). At the server it is part
    // of the super-admin tier (isSuperAdmin).
    adminActions: [
      A.VIEW_USERS,
      A.EDIT_USER,
      A.DELETE_USER,
      A.ACCESS_ADMIN_PANEL,
      A.MANAGE_AIDES,
      A.MANAGE_TEACHER_FEEDBACK,
      A.MANAGE_IMPORTS,
      A.MANAGE_BACKUPS
    ],
    edit: { scope: 'any', plan: null }
  },

  sped_chair: {
    label: 'SPED Chair',
    // No DELETE_USER: firestore.rules restrict user deletes to the super-admin
    // tier; sped chairs can create/edit users but not destroy accounts.
    adminActions: [
      A.VIEW_USERS,
      A.EDIT_USER,
      A.ACCESS_ADMIN_PANEL,
      A.MANAGE_AIDES,
      A.MANAGE_TEACHER_FEEDBACK
    ],
    edit: { scope: 'planType', plan: 'IEP' }
  },

  sped_chair_all: {
    label: 'SPED Chair (All)',
    adminActions: [
      A.VIEW_USERS,
      A.EDIT_USER,
      A.ACCESS_ADMIN_PANEL,
      A.MANAGE_AIDES,
      A.MANAGE_TEACHER_FEEDBACK
    ],
    // Views IEP + 504, edits IEP only.
    edit: { scope: 'planType', plan: 'IEP' }
  },

  case_manager: {
    label: 'Case Manager',
    adminActions: [A.VIEW_USERS, A.MANAGE_TEACHER_FEEDBACK],
    edit: { scope: 'caseload', plan: null }
  },

  case_manager_full: {
    label: 'Case Manager (All)',
    adminActions: [A.VIEW_USERS, A.MANAGE_TEACHER_FEEDBACK],
    edit: { scope: 'caseload', plan: null }
  },

  speech_case_manager: {
    label: 'Speech Case Manager',
    adminActions: [A.VIEW_USERS, A.MANAGE_TEACHER_FEEDBACK],
    edit: { scope: 'caseload', plan: null }
  },

  service_provider: {
    label: 'Service Provider',
    // View-only: serves students but cannot edit, and has no caseload.
    // NOTE: speech_case_manager IS a service-provider-type role but DOES have a
    // caseload and can edit it (see speech_case_manager below).
    adminActions: [A.VIEW_USERS],
    edit: { scope: 'none', plan: null }
  },

  school_psych: {
    label: 'School Psychologist',
    adminActions: [A.VIEW_USERS, A.MANAGE_TEACHER_FEEDBACK],
    edit: { scope: 'none', plan: null }
  },

  teacher: {
    label: 'Teacher',
    adminActions: [A.VIEW_USERS],
    edit: { scope: 'none', plan: null }
  },

  teacher_full: {
    label: 'Teacher (All)',
    adminActions: [A.VIEW_USERS],
    edit: { scope: 'none', plan: null }
  },

  paraeducator: {
    label: 'Paraeducator',
    adminActions: [A.VIEW_USERS],
    edit: { scope: 'none', plan: null }
  }
}

// Which roles may reach the counts-only District Dashboard (the `/district`
// route + the "District Dashboard" nav item, gated by DISTRICT_DASHBOARD_ROLES
// in config/roles.js). This is a DISTRICT-tier surface only: school `admin` and
// `school_admin` are scoped to a single school and must NOT have district
// access. Keep this in sync with DISTRICT_DASHBOARD_ROLES — the role contract
// smoke test asserts they match exactly.
export const EXPECTED_DISTRICT_DASHBOARD_ROLES = ['district_admin', 'district_coordinator']

// The admin-panel-level actions covered by the contract test (student-edit
// actions are validated separately via canEditStudent()).
export const ADMIN_PANEL_ACTIONS = [
  A.VIEW_USERS,
  A.EDIT_USER,
  A.DELETE_USER,
  A.ACCESS_ADMIN_PANEL,
  A.MANAGE_AIDES,
  A.MANAGE_SYSTEM_SETTINGS,
  A.MANAGE_TEACHER_FEEDBACK,
  A.MANAGE_IMPORTS,
  A.MANAGE_BACKUPS
]

// Known, intentionally-tracked drift between sources of truth. Roles listed
// here are EXEMPT from the strict matrix-vs-behavior consistency assertion, but
// the drift is documented (and surfaced as it.todo) so it can't be forgotten.
// Empty = all sources currently agree with this expectations table.
export const KNOWN_DRIFT = {
  staff_edit_docs: [
    'firestore.rules allow FULL student + studentPrivate writes server-side ' +
      '(isAnyStaffEdit); the client narrows this role to flags + documents ' +
      '(useStudentForm). Tighten rules with diff-limited writes in a future deploy.'
  ],
  school_psych: [
    'firestore.rules allow student CREATE (canCreateStudentLike) and ' +
      'studentPrivate writes (canWriteStudentPrivate) although the client ' +
      'treats this role as view-only-with-feedback. Revisit rules in a future deploy.'
  ]
}

// ── Feedback "send" capability ─────────────────────────────────────────────
// Independent statement of intent for who may SEND a teacher-feedback form for
// a given student (the icon on the Students page → RoleUtils.canSendFeedback,
// backed by FEEDBACK_ACCESS_RULES). This is a SEPARATE surface from the
// PERMISSIONS_MATRIX MANAGE_TEACHER_FEEDBACK admin action, and was the source of
// the admin_504 504-coordinator regression — so it gets its own contract.
//
// Feedback creation mirrors the MEETING model (canManageMeetings): only the
// student's case manager of record — every CM-assignable role — plus the 504
// coordinator for 504 students. Plain admins can no longer send feedback.
// The single exception is school psychologists, who may create feedback forms
// for ANY student (reevaluation feedback); the student's case manager is
// expected to be a shared viewer on psych-created forms.
//
// Context dimensions that matter:
//   isOwn   → the user is the student's case manager
//   teaches → (legacy; no longer grants feedback access)
//   plan    → student plan ('IEP' | '504' | null)
export function expectedCanSendFeedback(role, { isOwn, plan }) {
  switch (role) {
    // The reevaluation exception: psychs may create feedback for any student.
    case 'school_psych':
      return true

    // Case manager of record only — mirrors canManageMeetings.
    case 'case_manager':
    case 'case_manager_full':
    case 'speech_case_manager':
    case 'sped_chair':
    case 'sped_chair_all':
      return isOwn

    // 504 coordinator: own caseload OR any 504 student (mirrors meetings).
    case 'admin_504':
      return isOwn || plan === '504'

    // Everyone else cannot send feedback — including admin, district_admin,
    // and school_admin (mirrors meeting scheduling).
    default:
      return false
  }
}

/**
 * Resolve the expected canEditStudent() result for a role given a sample
 * student context. Mirrors the capability vocabulary above.
 */
export function expectedCanEdit(role, { isOwn, plan, isEvalCm = false }) {
  const exp = ROLE_CAPABILITY_EXPECTATIONS[role]?.edit
  if (!exp) return false
  switch (exp.scope) {
    case 'any':
      return true
    case 'none':
      return false
    case 'planType':
      return plan === exp.plan
    case 'caseload':
      return (isOwn || isEvalCm) && (exp.plan ? plan === exp.plan : true)
    default:
      return false
  }
}
